<div class="wrapper flex flex-col justify-between">
  <div class="heading text-type-2 mb-[20px] flex flex-row items-center justify-between">
    <div>{{ data?.editClaim?.claim_number || 'File New Claim' }}</div>
    @if (data?.editClaim?.id && ['draft', 'sent_back'].includes(data?.editClaim?.status)) {
      <button (click)="deleteClaim()" class="delete-icon-button" aria-label="Delete claim">
        <mat-icon svgIcon="trash" class="text-red-500" />
      </button>
    }
  </div>

  <mat-tab-group class="contents-tab-group flex-1" [selectedIndex]="selectedTabIndex" (selectedIndexChange)="selectedTabIndex = $event">
    <mat-tab label="Details">
  <div class="details flex-1 pt-[20px]">
    <app-dropdown
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['warranty_id']"
      [options]="warrantyOptions"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="warranty_id"
      label="warranty"
    />
    <app-text-input
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['title']"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="title"
      label="title"
    />
    <app-text-area
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['description']"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="description"
      label="description"
    />
    <app-dropdown
      (checkForErrors)="checkForErrors($event)"
      [enableSearch]="true"
      [error]="errorMessages['site_id']"
      [options]="sitesList"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="site_id"
      label="site"
    />
    <app-dropdown
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['structure_type']"
      [options]="structureTypeOptions"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="structure_type"
      label="structure type"
    />
    <app-dropdown
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['claim_type']"
      [options]="claimTypeOptions"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="claim_type"
      label="claim type"
    />
    @if (defectTypeMode === 'dropdown') {
      <app-dropdown
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['defect_type']"
        [options]="defectTypeOptions"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="defect_type"
        label="defect type"
      />
    }
    @if (defectTypeMode === 'text') {
      <app-text-input
        (checkForErrors)="checkForErrors($event)"
        [error]="errorMessages['defect_type']"
        [parentFormGroup]="form"
        class="w-full mb-[20px]"
        controlName="defect_type"
        label="defect type"
      />
    }
    <app-dropdown
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['severity']"
      [options]="severityOptions"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="severity"
      label="severity"
    />
    <app-date-picker
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['need_by_date']"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="need_by_date"
      label="need by date"
    />
    <app-text-area
      (checkForErrors)="checkForErrors($event)"
      [error]="errorMessages['proposed_solution']"
      [parentFormGroup]="form"
      class="w-full mb-[20px]"
      controlName="proposed_solution"
      label="proposed solution"
    />
    @if (latitude !== null && longitude !== null) {
      <app-location-section
        (onDelete)="deleteLocation()"
        [isEditable]="true"
        [latitude]="latitude"
        [longitude]="longitude"
        [mapFeatureLink]="mapFeatureLink ?? null"
        class="mb-[20px]"
      />
    }
    @if (mapFeatureLink) {
      <app-design-context
        class="block mb-[20px]"
        data-testid="warranty-claim-design-context"
        [link]="mapFeatureLink"
        [collapseWhenCaptured]="true"
        [editable]="true"
        [projectId]="data?.projectId"
        (remove)="removeDesignContext()"
        (editLocation)="openLocationPicker()"
        (linkChange)="onDesignContextLinkChange($event)"
      />
    }

    <!-- Linked Issues (when editing an existing claim) -->
    @if (linkedIssues.length) {
      <div class="issues-section mb-[20px]">
        <div class="issues-section-header flex items-center gap-[8px] mb-[12px]">
          <span class="section-label">Linked Issues</span>
          <span class="count-badge">{{ linkedIssues.length }}</span>
        </div>
        @for (issue of linkedIssues; track issue.id) {
          <div class="issue-item linked flex items-start gap-[12px] mb-[8px]">
            <div class="issue-info flex-1">
              <div class="issue-title text-type-11">{{ issue.title }}</div>
              <div class="issue-meta flex items-center gap-[8px] mt-[4px]">
                <span class="issue-type-badge">{{ issue.type || 'Issue' }}</span>
                <span class="issue-status-badge">{{ issueStatusText(issue.status) }}</span>
                <span class="issue-creator text-[#60698B]">{{ issue.created_by_first_name }} {{ issue.created_by_last_name }}</span>
              </div>
            </div>
            <button class="unlink-button" (click)="unlinkIssue(issue)" aria-label="Unlink issue">
              <mat-icon class="unlink-icon">link_off</mat-icon>
            </button>
          </div>
        }
      </div>
    }

    <!-- Nearby Issues (shown when location is set) -->
    @if (latitude !== null && longitude !== null) {
      <div class="issues-section mb-[20px]">
        <div class="issues-section-header flex items-center gap-[8px] mb-[12px]">
          <span class="section-label">Nearby Issues</span>
          @if (nearbyIssues.length) {
            <span class="count-badge">{{ nearbyIssues.length }}</span>
          }
        </div>
        @if (nearbyIssuesLoading) {
          <div class="text-type-14 text-[#60698B] py-[12px]">Loading nearby issues...</div>
        } @else if (nearbyIssues.length === 0) {
          <div class="text-type-14 text-[#60698B] py-[12px]">No issues found nearby.</div>
        } @else {
          @for (issue of nearbyIssues; track issue.id) {
            <div class="issue-item nearby flex items-start gap-[12px] mb-[8px]"
                 [class.disabled]="isIssueLinked(issue.id)">
              <mat-checkbox
                [checked]="isIssueSelected(issue.id) || isIssueLinked(issue.id)"
                [disabled]="isIssueLinked(issue.id)"
                (change)="toggleIssueSelection(issue.id)"
                color="primary"
              ></mat-checkbox>
              <div class="issue-info flex-1">
                <div class="issue-title text-type-11">{{ issue.title }}</div>
                @if (issue.description) {
                  <div class="issue-description text-type-14 text-[#60698B] mt-[2px]">{{ issue.description }}</div>
                }
                <div class="issue-meta flex items-center gap-[8px] mt-[4px] flex-wrap">
                  <span class="issue-type-badge">{{ issue.type_label || issue.type || 'Issue' }}</span>
                  <span class="issue-status-badge">{{ issueStatusText(issue.status) }}</span>
                  <span class="issue-distance text-[#60698B]">{{ formatDistance(issue.distance) }}</span>
                </div>
                @if (issue.pictures?.length) {
                  <div class="issue-pictures flex gap-[6px] mt-[6px] flex-wrap">
                    @for (pic of issue.pictures; track pic.id) {
                      <img [src]="pic.thumb_url || pic.original_url" class="issue-thumbnail" alt="Issue photo" />
                    }
                  </div>
                }
                @if (isIssueLinked(issue.id)) {
                  <div class="already-linked-label text-[#60698B] mt-[2px]">Already linked to this claim</div>
                }
              </div>
            </div>
          }
        }
      </div>
    }

    @if (mediaFiles?.length) {
      <app-pictures-section-with-tags
        (onDelete)="deletePicture($event)"
        [isEditable]="true"
        [pictures]="mediaFiles"
        class="pictures-section mb-[40px] mt-[30px]"
        label="Attachments"
      />
    }
  </div>
    </mat-tab>

    @if (data?.editClaim?.id) {
      <mat-tab label="Discussion">
        <div class="discussion-content pt-[20px]">
          @if (workflowHistory.length) {
            @for (entry of workflowHistory; track entry.date) {
              <div class="workflow-entry mb-[20px]">
                <div class="flex flex-row items-start gap-[12px]">
                  <div class="workflow-icon">
                    <mat-icon class="text-[#36AB19]">check_circle</mat-icon>
                  </div>
                  <div class="flex flex-col flex-1">
                    <div class="entry-action text-type-8">{{ entry.action }}</div>
                    <div class="flex flex-row items-center gap-[8px] mt-[8px]">
                      <div class="avatar">{{ entry.actor_initials }}</div>
                      <div class="flex flex-col">
                        <span class="text-type-11">{{ entry.actor_name }}</span>
                      </div>
                    </div>
                    <div class="text-type-14 text-[#60698B] mt-[4px]">
                      {{ entry.date | date:'MMM d, y' }} &bull; {{ entry.date | date:'h:mm a' }}
                    </div>
                    @if (entry.notes) {
                      <div class="comment-section mt-[12px]">
                        <div class="comment-label text-type-13 uppercase">Comment:</div>
                        <div class="comment-text text-type-11 mt-[4px]">{{ entry.notes }}</div>
                      </div>
                    }
                  </div>
                </div>
                <hr class="entry-divider" />
              </div>
            }
          } @else {
            <div class="no-data text-type-14 text-[#60698B] text-center py-[40px]">No activity yet.</div>
          }
        </div>
      </mat-tab>
    }
  </mat-tab-group>

  <div class="action-section mb-[5px] flex flex-row gap-[20px] w-full">
    <div [matMenuTriggerFor]="add" class="action-button add-button">
      <mat-icon class="add" svgIcon="plus"></mat-icon>
    </div>
    @if (isAuthoringClaim()) {
      <span
        class="flex-1 flex"
        [matTooltip]="!isWarrantyActive() ? 'Warranty is not active — claims cannot be created or updated.' : ''"
        matTooltipClass="white-tooltip">
        <app-secondary-button (handleClick)="save()" [isDisabled]="!isFormDirty || !isWarrantyActive()" class="flex-1">Save
        </app-secondary-button>
      </span>
      <span
        class="flex-1 flex"
        [matTooltip]="!isWarrantyActive() ? 'Warranty is not active — claims cannot be created or updated.' : ''"
        matTooltipClass="white-tooltip">
        <app-primary-button (handleClick)="submit()" [isDisabled]="!form.valid || !isWarrantyActive()" class="flex-1">Submit
        </app-primary-button>
      </span>
    } @else {
      <!-- Claim is in flight: an admin may correct the details, but the status
           only moves via the workflow actions on the detail panel. -->
      <span
        class="flex-1 flex"
        [matTooltip]="!isWarrantyActive() ? 'Warranty is not active — claims cannot be created or updated.' : ''"
        matTooltipClass="white-tooltip">
        <app-primary-button (handleClick)="save()" [isDisabled]="!isFormDirty || !form.valid || !isWarrantyActive()" class="flex-1">Save changes
        </app-primary-button>
      </span>
    }
  </div>
</div>

<input
  #fileInput
  (change)="onFileSelected($event)"
  class="hidden"
  multiple="multiple"
  type="file"
  accept="image/*,application/pdf,.pdf" />
<mat-menu #add="matMenu">
  @for (option of addMenuOptions; track option) {
    <button
      (click)="option.handler()"
      mat-menu-item
      >
      <div class="add-menu-item flex justify-start items-center">
        <div class="flex justify-center items-center h-[48px]">
          <mat-icon [svgIcon]="option.icon" class="menu-icon" />
        </div>
        <div class="menu-label">{{ option.label }}</div>
      </div>
    </button>
  }
</mat-menu>
